import type { ObjectCustomerType } from '@econtractor/types';

export const EMPTY_CUSTOMER: ObjectCustomerType = {
    personType: '',
    fullName: '',
    edrpouOrIpn: '',
    vatEnabled: false,
    vatNumber: '',
    iban: '',
    bank: '',
    phone: '',
    email: '',
    legalAddress: '',
    representativeName: '',
    representativePosition: '',
};

export type YouControlUsr = {
    status?: string;
    name?: string | { fullName?: string; shortName?: string };
    address?: string;
    contacts?: {
        phone?: string | null;
        additionalPhone?: string | null;
        email?: string | null;
        webSite?: string | null;
        website?: string | null;
        otherContacts?: string | null;
    };
    signers?: Array<{
        name?: string | null;
        role?: string | null;
        description?: string | null;
    }>;
    contractorType?: string;
};

export const parseObjectCustomer = (rawValue: unknown): ObjectCustomerType => {
    if (!rawValue) return { ...EMPTY_CUSTOMER };
    if (typeof rawValue === 'object' && !Array.isArray(rawValue)) {
        return { ...EMPTY_CUSTOMER, ...(rawValue as ObjectCustomerType) };
    }
    if (typeof rawValue !== 'string') return { ...EMPTY_CUSTOMER };

    try {
        const parsed = JSON.parse(rawValue);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? { ...EMPTY_CUSTOMER, ...(parsed as ObjectCustomerType) }
            : { ...EMPTY_CUSTOMER };
    } catch (_e) {
        return { ...EMPTY_CUSTOMER };
    }
};

export const normalizeCustomerPayload = (formValues: ObjectCustomerType): ObjectCustomerType => ({
    personType: String(formValues.personType || '').trim(),
    fullName: String(formValues.fullName || '').trim(),
    edrpouOrIpn: String(formValues.edrpouOrIpn || '').trim(),
    vatEnabled: Boolean(formValues.vatEnabled),
    vatNumber: String(formValues.vatNumber || '').trim(),
    iban: String(formValues.iban || '').trim(),
    bank: String(formValues.bank || '').trim(),
    phone: String(formValues.phone || '').trim(),
    email: String(formValues.email || '').trim(),
    legalAddress: String(formValues.legalAddress || '').trim(),
    representativeName: String(formValues.representativeName || '').trim(),
    representativePosition: String(formValues.representativePosition || '').trim(),
});

const pickText = (...values: Array<unknown>): string => {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
};

/** Маппінг сирої відповіді YouControl → поля форми замовника */
export const mapYouControlToCustomer = (
    yc: YouControlUsr,
    companyCode: string,
): Partial<ObjectCustomerType> => {
    const code = String(companyCode || '').trim();
    const digits = code.replace(/\D/g, '');

    const fullName =
        typeof yc?.name === 'string'
            ? yc.name.trim()
            : pickText(yc?.name?.fullName, yc?.name?.shortName);

    const firstSigner = Array.isArray(yc?.signers) ? yc.signers[0] : null;
    const contractorType = String(yc?.contractorType || '').toLowerCase();

    let personType: string | undefined;
    if (contractorType.includes('фоп') || contractorType.includes('фізич') || digits.length === 10) {
        personType = 'individual';
    } else if (digits.length === 8 || fullName) {
        personType = 'legal';
    }

    return {
        ...(personType ? { personType } : {}),
        ...(fullName ? { fullName } : {}),
        ...(code ? { edrpouOrIpn: code } : {}),
        ...(pickText(yc?.contacts?.phone, yc?.contacts?.additionalPhone)
            ? { phone: pickText(yc?.contacts?.phone, yc?.contacts?.additionalPhone) }
            : {}),
        ...(pickText(yc?.contacts?.email) ? { email: pickText(yc?.contacts?.email) } : {}),
        ...(pickText(yc?.address) ? { legalAddress: pickText(yc?.address) } : {}),
        ...(pickText(firstSigner?.name) ? { representativeName: pickText(firstSigner?.name) } : {}),
        ...(pickText(firstSigner?.role)
            ? { representativePosition: pickText(firstSigner?.role) }
            : {}),
    };
};
